import type { PostHog } from 'posthog-js'
import { useEffect, useState } from 'react'

type RequestIdleCallbackFn = (callback: IdleRequestCallback, options?: IdleRequestOptions) => number

const isDevelopment = import.meta.env.DEV
const isProduction = import.meta.env.PROD

// PostHog configuration
const posthogConfig = {
  api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com',
  person_profiles: 'identified_only' as const,
  capture_pageview: false, // We'll handle this manually
  capture_pageleave: true,
  loaded: () => {
    if (isDevelopment) {
      console.log('PostHog loaded')
    }
  },
}

let posthogRef: PostHog | null = null
async function ensurePosthog() {
  if (posthogRef) return posthogRef
  const { default: posthog } = await import('posthog-js')
  posthogRef = posthog
  return posthogRef
}

// Defer PostHog initialization to idle/interaction
if (typeof window !== 'undefined' && import.meta.env.VITE_POSTHOG_KEY) {
  const init = async () => {
    const ph = await ensurePosthog()
    ph.init(import.meta.env.VITE_POSTHOG_KEY!, posthogConfig)
  }
  const schedule = () => {
    const requestIdleCallback = (window as Window & { requestIdleCallback?: RequestIdleCallbackFn })
      .requestIdleCallback
    if (requestIdleCallback) {
      requestIdleCallback(init)
    } else {
      setTimeout(init, 0)
    }
  }
  // First schedule on idle
  schedule()
  // Also on first user interaction if still not initialized
  const onFirstInput = () => {
    init()
    window.removeEventListener('pointerdown', onFirstInput)
    window.removeEventListener('keydown', onFirstInput)
  }
  window.addEventListener('pointerdown', onFirstInput, { once: true })
  window.addEventListener('keydown', onFirstInput, { once: true })
}

/**
 * PostHog provider component for marketing analytics
 */
export function PostHogProviderWrapper({ children }: { children: React.ReactNode }) {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted) {
    return <>{children}</>
  }

  // Avoid pulling React PostHog provider into the main bundle; we rely on JS API
  return <>{children}</>
}

/**
 * Hook to track page views
 */
export function usePageView() {
  useEffect(() => {
    if (isProduction && typeof window !== 'undefined') {
      ensurePosthog().then((ph) => ph.capture('$pageview'))
    }
  }, [])
}

/**
 * Hook to track custom events
 */
export function usePostHog() {
  return {
    track: (event: string, properties?: Record<string, unknown>) => {
      if (isProduction && typeof window !== 'undefined') {
        ensurePosthog().then((ph) => ph.capture(event, properties))
      }
    },
    identify: (userId: string, properties?: Record<string, unknown>) => {
      ensurePosthog().then((ph) => ph.identify(userId, properties))
    },
    reset: () => {
      ensurePosthog().then((ph) => ph.reset())
    },
  }
}
